/**
 * Mod's own skills (RFC 0334 3b): the how-to for each of Mod's capabilities.
 *
 * WHY SKILLS, NOT PROMPT TEXT. The how-to used to be `<mod_capabilities>`, a
 * block built in code and paid for on every turn, which nobody could read or
 * change without a release. What every agent needs every turn stays there, and
 * short (`capability-guide.ts`); the rest is here, listed with every other
 * skill and read when a task needs it. A World or project skill with the same
 * id replaces one of these, so a team teaches its own way of running tasks
 * without forking Mod.
 *
 * TEXT SHIPS IN CORE. A Mod skill has no document on the log: it changes with
 * the release, not with an edit. Its id is `mod:<skillId>`, which
 * `read_document` and a `/` pick both resolve here (`modSkillText`), so an
 * agent reads it the way it reads any skill.
 *
 * GATED ON THE TOOLS IT TEACHES. `tools` is every tool a skill tells the agent
 * to call, compared on the short name, so a read-only session is never taught
 * to create tasks. A capability's reads are not listed: an agent that can
 * only look needs the standing block, not the how-to.
 */

/**
 * A skill that ships with Mod. Offered only when the session holds every tool
 * in `tools`, so a read-only agent is never taught to write.
 */
export interface ModSkill {
  skillId: string
  title: string
  description: string
  documentId: string
  tools: readonly string[]
  /** The instructions. Absent only in a test fixture. */
  text?: string
}

/** The id prefix `read_document` resolves from core instead of the repo. */
export const MOD_SKILL_ID_PREFIX = 'mod:'

const skill = (
  skillId: string,
  title: string,
  description: string,
  tools: readonly string[],
  lines: readonly string[],
): ModSkill => ({
  skillId,
  title,
  description,
  documentId: `${MOD_SKILL_ID_PREFIX}${skillId}`,
  tools,
  text: `# ${title}\n\n${lines.join('\n')}\n`,
})

export const MOD_SKILLS: readonly ModSkill[] = [
  skill(
    'mod-tasks',
    'Mod: tasks',
    'Planning, starting, tracking or finishing work as Mod tasks: when to make one, how to mark it active, what a completion note says.',
    [
      'create_task',
      'update_task',
      'complete_task',
      'read_task',
      'list_tasks',
      'my_tasks',
    ],
    [
      'Tasks live on the session log, not in your context: a task is visible in the project to everyone in it, on every device, after this turn ends. A plan you keep to yourself is visible to nobody and dies with the turn.',
      '',
      '- Read the whole task before acting on it: read_task carries the description, dependencies and subtasks. A title is a label, not a specification.',
      '- Mark a task active when you start it. That status is how someone watching tells work that is happening from work merely written down.',
      '- Complete with a note that says something: what changed, what validation actually ran, what risk is left. Not "done": the status already says that, and the note is the one field where the next person could have learned something.',
      '- Look before you add: list_tasks first. Updating related work beats a near-duplicate that splits the record in two.',
      "- Create a task for work that OUTLIVES this turn: a plan you are about to execute over several steps, a follow-up you are deliberately not doing now, work you are handing on. Not for a step you finish in this same turn: a task created and completed in one breath is noise in everyone else's sidebar.",
      '- my_tasks is the session lens: what THIS session is working on, which is what someone means when they ask what you are doing.',
    ],
  ),
  skill(
    'mod-sessions',
    'Mod: sessions',
    'Working with other sessions in the project: seeing what they are doing, starting one for separate work, handing work to one, answering one that is waiting, filing one as done, naming this one.',
    [
      'start_session',
      'send_message',
      'list_sessions',
      'read_session',
      'answer_question',
      'complete_session',
      'name_session',
    ],
    [
      'Other sessions in this project are running work of their own, and you can see them: list_sessions gives every session, and withState says what each is DOING. "asking" is the one that matters, because that session has stopped and stays stopped until a person answers it.',
      '',
      '- read_session before acting on one. It carries the transcript and, when the session is waiting, the question and the interactionId to answer it.',
      '- start_session opens a NEW session for work that deserves its own thread, and gives it its first instruction. Prefer it over doing a second, unrelated job inside this one: separate work in separate sessions can be watched, steered and finished separately.',
      '- send_message hands work to ANOTHER session, as if the person typed it there. Check what it reports: "queued" means the message landed but nothing picked it up, which is not the same as work starting.',
      '- Do the work in the session it belongs to. A session that owns a piece of work has the context for it; pulling that work into this thread loses the context and hides it from whoever is watching that session.',
      '- answer_question unblocks a waiting session, but answer for the person only when you already know what they want. Guessing commits them to a choice they never made, and the agent acts on it immediately.',
      '- complete_session files a session as done so it leaves the open list. It records a DECISION, not an event: an agent going quiet is not the same as its work being finished.',
      '- name_session names THIS session once, while its title is still a placeholder. Name the bucket the work belongs to, as the person calls it, not the task. A title a person chose is never replaced.',
    ],
  ),
  skill(
    'mod-questions',
    'Mod: asking the person',
    'Deciding whether to ask the person a question or decide yourself, and how to ask one well.',
    ['ask_user_question'],
    [
      'A question is written to the session log and can be answered from any device syncing this project, so the answer may arrive from a phone while you wait. Because it reaches a human and costs their attention, ask when a wrong guess would be expensive or hard to undo, and otherwise decide, say what you assumed, and continue.',
      '',
      '- Ask one question at a time, with your pick first and marked as recommended.',
      '- An ask is a status, not a stop: carry on with whatever does not depend on the answer.',
      '- Put a long proposal in a document and ask about it; text before a question is easy to miss.',
    ],
  ),
  skill(
    'mod-documents',
    'Mod: documents',
    "Writing, editing or organising a Mod document: when to make one instead of a long message, how to change one without losing someone else's edit, and how to move documents between folders.",
    [
      'create_document',
      'patch_document',
      'read_document',
      'update_document',
      'move_folder',
    ],
    [
      'Documents are project objects, not files on your disk: one is immediately open-able by everyone in the project. Prefer a document over a long message for anything meant to be kept, referred back to, or edited by someone else.',
      '',
      '- Edit with patch_document. Rewriting a whole document to change a paragraph discards concurrent edits somebody else is making in it.',
      '- read_document before you patch: oldString must match the text exactly, and the read also hands you the open comments on it.',
      '- Editing a file in a repository does not change a document, and the reverse. Say which one you changed.',
      "- A folder is the start of its documents' paths. Move one document with update_document and `path`, a whole folder with move_folder, and either to another project with `toProject`; ids stay the same, so links, comments and open panes follow. Read list_documents first, move only what the person pointed at, leave an app's own folders (apps/) alone, and say in one line what moved.",
    ],
  ),
  skill(
    'mod-skills',
    'Mod: skills',
    'Writing down how a kind of task should be done as a skill, choosing where it lives, and changing one.',
    ['register_skill', 'patch_document'],
    [
      'A skill is instructions for a KIND of task: how plans get written here, how a review is run, what a good commit looks like. When the person corrects how you did something and the correction would apply next time too, offer to write it down as a skill. Guidance repeated in a conversation dies with it.',
      '',
      '- A World skill applies in every project the person works in; a project skill applies in one project and is shared with everyone in it. register_skill writes a World skill unless you pass scope "project".',
      '- A skill with the same id as one further out replaces it: project over World over Mod. That is how a team changes one of these mod- skills.',
      '- A skill guides HOW to work. The task, RFC slice or review flag defines the outcome; tests, review and evals are evidence that it is done. Skills may propose clearer acceptance criteria, but never silently replace the shared criteria or decide completion.',
      '- The description is how a skill gets CHOSEN, and it is all any agent sees until it opens the document. Describe the task it applies to, not the document.',
      '- Change a skill by patching its document. Registering the same skill again rewrites it wholesale, which throws away any edit someone else has made to it since.',
    ],
  ),
  skill(
    'mod-comments',
    'Mod: comments',
    'Saying something about a document, as a question, caveat or suggestion, instead of editing it; replying to and resolving comments.',
    ['comment'],
    [
      'A comment attaches to a document and shows in its side column. Use one to say something ABOUT a document, a question, a caveat, a suggestion, rather than editing the document to talk in it.',
      '',
      '- Reply in the thread you were asked in, not with a new comment beside it.',
      '- When you make the change a comment asked for, reply saying what you changed, so the person can resolve it.',
      '- Answer every open comment you were pointed at, not only the first.',
    ],
  ),
  skill(
    'mod-pairing',
    'Mod: pairing on a document',
    'A turn that started from a comment or an edit on a document you are paired to, with no message: deciding whether to say anything.',
    ['pass'],
    [
      'A person can pair you to a document. Then a turn can start without a message: a comment on that document, and later an edit to it. You decide what the moment needs, and often it is nothing.',
      '',
      '- When there is nothing worth saying, call pass and end the turn without writing. A reply that adds nothing costs the person a read.',
      '- An edit that breaks something, a paste that lost its formatting, a broken link, is worth fixing; a draft in progress is not worth commenting on.',
    ],
  ),
  skill(
    'mod-spaces',
    'Mod: spaces, panes and the view',
    'Showing the person something: opening a document, app or session in their window, reading what they are looking at, arranging panes in a space.',
    ['read_view', 'set_view', 'control_app'],
    [
      'The person works in a window. A space is one arrangement of panes in it, and each pane shows a conversation, the session list, a document or an app. What they are looking at is what "this" means in their message.',
      '',
      '- read_view before you guess what "this page" is. It names the space, the panes and how they are arranged.',
      '- Open the page the work needs without being asked. When you write a document, change an app or start a review, set_view it into a pane so the person sees it, rather than pasting a link.',
      '- Add a pane beside the conversation; do not replace the pane the person is using.',
      '- Making or closing a space changes the World; moving within one does not. Do the first only when the person asked for it.',
      '- control_app operates the app in the focused pane through the commands it declares. Call it with no action first to read the manifest.',
    ],
  ),
  skill(
    'mod-apps',
    'Mod: apps',
    'Finding what an installed app can do, using it by writing its records, and changing an app.',
    ['list_apps', 'read_app', 'update_app', 'read_document'],
    [
      "An app is a view over typed documents: its records. Weekly's week, a review, a deck are records. Each app has a skill that says what its records mean and how to change them.",
      '',
      "- list_apps shows what is installed; read_app gives an app's schema, its view and its skill document. An app open beside the conversation already has its skill in your turn; do not read it again.",
      "- Read the app's skill before writing one of its records. Guessing a field shape writes a record the view cannot draw.",
      '- Use an app by writing its records with the document tools. read_document on a record returns its fields as JSON.',
      '- update_app changes the app itself: its name, schema or view. Change an app only when the person asked for the app to change, not to get one record right.',
    ],
  ),
]

/** A Mod skill's text by its document id (`mod:<skillId>`), or null. */
export function modSkillText(documentId: string): string | null {
  if (!documentId.startsWith(MOD_SKILL_ID_PREFIX)) return null
  const found = MOD_SKILLS.find((s) => s.documentId === documentId)
  return found?.text ?? null
}

/**
 * The short name a tool is gated on. A harness prefixes MCP tools
 * (`mcp__mod__create_task`), and Claude Code services its native
 * `AskUserQuestion` where other providers get the MCP `ask_user_question`.
 */
export function shortToolName(name: string): string {
  const short = name.split('__').pop() ?? name
  return short === 'AskUserQuestion' ? 'ask_user_question' : short
}
